import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SendIcon from "@mui/icons-material/Send";
import InboxIcon from "@mui/icons-material/Inbox";
import { config } from "_helpers";

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString("it-IT") : "-";

const StatusChip = ({ confirmedAt, refusedAt }) => {
  if (confirmedAt)
    return <Chip label="Confermato" color="success" size="small" variant="filled" />;
  if (refusedAt)
    return <Chip label="Rifiutato" color="error" size="small" variant="filled" />;
  return <Chip label="In attesa" color="default" size="small" variant="outlined" />;
};


const ShiftRequestsDialog = ({
  outRequestDataSource = [],
  inRequestDataSource = [],
  onCommandClick,
  senderRowDataBound,
  recieverRowDataBound,
  handleClose,
  isOpen,
  translate,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < config.mobileSizeCheck);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < config.mobileSizeCheck);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Replica la logica di senderRowDataBound:
  // disabilita azioni se già confermato, rifiutato o data scaduta
  const isSenderRowDisabled = (row) => {
    const args = { data: row, row: { querySelectorAll: () => [{ classList: { add: () => {} }, style: {} }] } };
    return row.confirmedAt !== null || row.refusedAt !== null;
  };

  const isReceiverRowDisabled = (row) => {
    return row.confirmedAt !== null || row.refusedAt !== null;
  };

  // DESKTOP: tabella MUI
  const renderDesktopTable = (data, isSender) => (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold', color: '#2c5282' }}>
              {translate(isSender ? "fromMyDescription" : "fromOtherDescription")}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#2c5282' }}>
              {translate(isSender ? "toMyDescription" : "toOtherDescription")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#2c5282' }}>
              Stato
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#2c5282' }}>
              {isSender ? translate("cancelCmd") : translate("confirmCmd")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => {
            const disabled = isSender ? isSenderRowDisabled(row) : isReceiverRowDisabled(row);
            return (
              <TableRow
                key={idx}
                sx={{
                  opacity: disabled ? 0.5 : 1,
                  '&:hover': { bgcolor: '#f0f7ff' },
                  '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                }}
              >
                <TableCell>
                  <Typography variant="body2">{row.fromDescription}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.toDescription || '-'}</Typography>
                </TableCell>
                <TableCell align="center">
                  <StatusChip confirmedAt={row.confirmedAt} refusedAt={row.refusedAt} />
                </TableCell>
                <TableCell align="center">
                  {isSender ? (
                    <Tooltip title={translate("delete")}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={disabled}
                          onClick={() => onCommandClick({ commandColumn: { type: "Delete" }, rowData: row })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title={translate("yes")}>
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={disabled}
                            onClick={() => onCommandClick({ commandColumn: { type: "Yes" }, rowData: row })}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={translate("no")}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={disabled}
                            onClick={() => onCommandClick({ commandColumn: { type: "No" }, rowData: row })}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // MOBILE: card MUI
  const renderMobileCards = (data, isSender) => (
    <Stack spacing={1.5}>
      {data.map((row, idx) => {
        const disabled = isSender ? isSenderRowDisabled(row) : isReceiverRowDisabled(row);
        return (
          <Paper
            key={idx}
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <StatusChip confirmedAt={row.confirmedAt} refusedAt={row.refusedAt} />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {isSender ? (
                  <Tooltip title={translate("delete")}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={disabled}
                        onClick={() => onCommandClick({ commandColumn: { type: "Delete" }, rowData: row })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip title={translate("yes")}>
                      <span>
                        <IconButton
                          size="small"
                          color="success"
                          disabled={disabled}
                          onClick={() => onCommandClick({ commandColumn: { type: "Yes" }, rowData: row })}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={translate("no")}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={disabled}
                          onClick={() => onCommandClick({ commandColumn: { type: "No" }, rowData: row })}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong style={{ color: '#2c5282' }}>
                {translate(isSender ? "fromMyDescription" : "fromOtherDescription")}:
              </strong>{" "}
              {row.fromDescription}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong style={{ color: '#2c5282' }}>
                {translate(isSender ? "toMyDescription" : "toOtherDescription")}:
              </strong>{" "}
              {row.toDescription || '-'}
            </Typography>
            {row.confirmedAt && (
              <Typography variant="caption" color="text.secondary">
                Confermato il: {formatDate(row.confirmedAt)}
              </Typography>
            )}
            {row.refusedAt && (
              <Typography variant="caption" color="text.secondary">
                Rifiutato il: {formatDate(row.refusedAt)}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Stack>
  );

  const renderSection = (data, isSender) => (
    <Box sx={{ mb: 2 }}>
      {/* Header sezione */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1.5,
        p: 1,
        bgcolor: isSender ? '#e3f2fd' : '#e8f5e9',
        borderRadius: 1,
        border: '1px solid',
        borderColor: isSender ? 'primary.light' : 'success.light',
      }}>
        {isSender
          ? <SendIcon fontSize="small" color="primary" />
          : <InboxIcon fontSize="small" color="success" />
        }
        <Typography variant="subtitle2" fontWeight="bold" color={isSender ? 'primary.main' : 'success.main'}>
          {translate(isSender ? "sentReqs" : "recievedReqs")}
        </Typography>
        <Chip
          label={data.length}
          size="small"
          color={isSender ? "primary" : "success"}
          variant="outlined"
          sx={{ ml: 'auto', height: 20 }}
        />
      </Box>

      {data.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          Nessuna richiesta
        </Typography>
      ) : (
        isMobile ? renderMobileCards(data, isSender) : renderDesktopTable(data, isSender)
      )}
    </Box>
  );

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md">
      {/* HEADER */}
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1.5,
      }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', flex: 1 }}>
          {translate("Richieste di Cambio Turno")}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* CONTENUTO */}
      <DialogContent sx={{ pt: '16px !important' }}>
        {outRequestDataSource?.length === 0 && inRequestDataSource?.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CompareArrowsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Nessuna richiesta di cambio turno
            </Typography>
          </Box>
        ) : (
          <>
            {renderSection(outRequestDataSource, true)}
            {outRequestDataSource?.length > 0 && inRequestDataSource?.length > 0 && (
              <Divider sx={{ my: 2 }} />
            )}
            {renderSection(inRequestDataSource, false)}
          </>
        )}
      </DialogContent>

      {/* FOOTER */}
      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} variant="contained" color="primary">
          {translate("okBtn")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShiftRequestsDialog;